// frontend/src/pages/Dashboard.js
import React, { useEffect, useState, useMemo } from "react"; 
import {
    LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { fetchAllSensorData } from "../utils/api"; 
import "../assets/styles/global.css";
import "../assets/styles/dashboard.css";
import io from 'socket.io-client'; // ✅ SocketIO Client

// Define the Socket.IO server URL (Must match your Flask app.py)
const SOCKET_URL = process.env.REACT_APP_BACKEND_URL + "";

// Helper function to format timestamp (Kept as is)
const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    return !isNaN(date) 
        ? date.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit', 
              fractionalSecondDigits: 3 // ✅ show milliseconds
          }) 
        : 'Invalid Time';
};


// Custom Tooltip for Charts (Kept as is)
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="glass-card p-3 border border-gray-700 shadow-lg" style={{ backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30, 30, 40, 0.8)' }}>
                <p className="label text-sm text-gray-400">{`${formatTime(label)}`}</p>
                {payload.map((entry, index) => (
                    <p key={`item-${index}`} style={{ color: entry.color }} className="intro text-sm font-semibold">
                        {`${entry.name} : ${entry.value !== null && entry.value !== undefined ? entry.value.toFixed(2) : 'N/A'}`}
                         {entry.name === 'Temperature (°C)' ? ' °C' : entry.name === 'Humidity (%)' ? ' %' : entry.name === 'Pressure (hPa)' ? ' hPa' : ''}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

// ✅ NEW: Aggregation Logic for Stats Cards
const calculateAggregates = (deviceData) => {
    const validData = deviceData.filter(d => d && typeof d.temperature === 'number');
    if (validData.length === 0) {
        return { avgTemp: '--', avgHumidity: '--', avgPressure: '--', maxTemp: '--', minHumidity: '--' };
    }

    const stats = {
        temp: validData.map(d => d.temperature),
        humidity: validData.map(d => d.humidity),
        pressure: validData.map(d => d.pressure),
    };

    const sumReducer = (acc, val) => acc + val;

    return {
        // Averages (sum / count)
        avgTemp: (stats.temp.reduce(sumReducer, 0) / stats.temp.length).toFixed(2),
        avgHumidity: (stats.humidity.reduce(sumReducer, 0) / stats.humidity.length).toFixed(2),
        avgPressure: (stats.pressure.reduce(sumReducer, 0) / stats.pressure.length).toFixed(2),
        // Min/Max for Smarter Alerts
        maxTemp: Math.max(...stats.temp).toFixed(2),
        minHumidity: Math.min(...stats.humidity).toFixed(2),
    };
};


export default function Dashboard() {
    const [allData, setAllData] = useState([]); // Store all fetched data
    const [selectedDevice, setSelectedDevice] = useState("all");
    const [isSocketConnected, setIsSocketConnected] = useState(false); 
    const [isLoadingHistory, setIsLoadingHistory] = useState(true); 

    // --- WEB SOCKET IMPLEMENTATION ---
    // frontend/src/pages/Dashboard.js

    // --- WEB SOCKET IMPLEMENTATION ---
    useEffect(() => {
        const socket = io(SOCKET_URL);
        const MAX_TOTAL_RECORDS = 500; 
        const token = localStorage.getItem('piwatch_token');
        // 1. Initial history load (HTTP FETCH - run once)
        const loadHistory = async () => {
            const result = await fetchAllSensorData();
            if (Array.isArray(result)) {
                result.sort((a, b) => a.timestamp - b.timestamp);
                setAllData(result); // Just set the history directly on first load
            }
            setIsLoadingHistory(false);
        };

        loadHistory(); 

        // --- ✅ DEFINE HANDLERS SO WE CAN REMOVE THEM LATER ---
        
        // --- DEFINE HANDLERS ---
       const onConnect = () => {
            console.log("Socket.IO: Connected to server. Authenticating...");
            setIsSocketConnected(true); // ✅ <--- ADD THIS LINE BACK
            
            // Emit the token to join the private room
            if (token) {
                socket.emit('authenticate', token);
            } else {
                console.error("Socket.IO: No auth token found, cannot authenticate socket.");
            }
        };

        const onDisconnect = () => {
            console.log("Socket.IO: Disconnected.");
            setIsSocketConnected(false);
        };

        const onNewSensorData = (newReading) => {
            if (!newReading || !newReading.device_id || typeof newReading.timestamp !== 'number') return;
            
            setAllData(prevData => {
                const key = newReading.device_id + '_' + newReading.timestamp;
                const existingKeys = new Set(prevData.map(d => d.device_id + '_' + d.timestamp));
                if (existingKeys.has(key)) return prevData; // skip duplicate
                
                const updatedData = [...prevData, newReading];
                return updatedData.slice(-MAX_TOTAL_RECORDS); // keep latest 500
            });
        };
        
        // 2. Attach Listeners
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('new_sensor_data', onNewSensorData);

        // 3. Cleanup on unmount
        return () => {
            console.log("Socket.IO: Cleaning up listeners and disconnecting.");
            
            // ✅ THE CRUCIAL FIX:
            // Explicitly remove these exact listeners
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('new_sensor_data', onNewSensorData);
            
            socket.disconnect();
        };
    }, []); // Empty dependency array. This runs ONCE per mount.

    // --- Process data using useMemo for efficiency (Kept as is) ---
    const { devices, deviceIds } = useMemo(() => {
        const groupedDevices = {};
        if (Array.isArray(allData)) {
            allData.forEach((row) => {
                if (row && row.device_id) {
                    if (!groupedDevices[row.device_id]) groupedDevices[row.device_id] = [];
                    if (row.timestamp && typeof row.timestamp === 'number') {
                       groupedDevices[row.device_id].push(row);
                    }
                }
            });
        }
        for (const deviceId in groupedDevices) {
            groupedDevices[deviceId].sort((a, b) => a.timestamp - b.timestamp);
        }
        return { devices: groupedDevices, deviceIds: Object.keys(groupedDevices) };
    }, [allData]); 

    const filteredDeviceIds = selectedDevice === "all"
        ? deviceIds
        : deviceIds.filter((id) => id === selectedDevice);
        
    const isDashboardLoading = isLoadingHistory && !isSocketConnected;


    // --- Render ---
    return (
        <div className="min-h-screen bg-darkBg text-white p-6 transition-all duration-500">
            {/* Header */}
            <header className="mb-10 text-center">
                <h1 className="text-5xl font-extrabold text-neonPink animate-pulse custom-glow-text">
                    ⚡ PiWatch Dashboard
                </h1>
                <p className={`mt-2 text-lg font-semibold ${isSocketConnected ? 'text-green-400' : 'text-yellow-400'}`}>
                    {isSocketConnected ? '● Connected (Real-time)' : '● Connecting via SocketIO...'}
                </p>
                {/* Device Filter Dropdown (Kept as is) */}
                <div className="mt-6 flex justify-center">
                    <select
                        value={selectedDevice}
                        onChange={(e) => setSelectedDevice(e.target.value)}
                        className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-neonBlue focus:outline-none focus:ring-2 focus:ring-neonPink transition-all"
                        disabled={isDashboardLoading || deviceIds.length === 0}
                    >
                        <option value="all">🌐 All Devices ({deviceIds.length})</option>
                        {deviceIds.map((id) => (
                            <option key={id} value={id}>🛰️ {id}</option>
                        ))}
                    </select>
                </div>
            </header>

            {/* Loading or No Devices Message */}
            {isDashboardLoading ? (
                <p className="text-center text-gray-400 mt-10 text-xl">Loading initial data history...</p>
            ) : deviceIds.length === 0 ? (
                <p className="text-center text-gray-400 mt-10 text-xl">
                    No active devices found for your account. Add devices in the 'Devices' section.
                </p>
            ) : (
                // Map through filtered devices to render sections
                filteredDeviceIds.map((deviceId) => {
                    const deviceData = devices[deviceId] || [];
                    const latestReading = deviceData.length > 0 ? deviceData[deviceData.length - 1] : null;

                    // ✅ NEW: Calculate Aggregates for Cards
                    const aggregates = calculateAggregates(deviceData); 

                    return (
                        <div key={deviceId} className="mb-16">
                            {/* Device Title */}
                            {selectedDevice === "all" && (
                                <h2 className="text-3xl font-bold text-neonBlue mb-6 custom-glow-text">
                                    🛰️ Device: {deviceId}
                                </h2>
                            )}

                            {/* --- Stats Cards (Now smarter) --- */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10"> 
                                <div className="glass-card glow-border text-center p-6 transform hover:scale-105 transition-transform duration-300">
                                    <h3 className="text-neonBlue text-xl font-bold mb-2">🌡 Temperature (Max)</h3> {/* Changed to MAX */}
                                    <p className="text-4xl text-orange-400 font-extrabold animate-glow">
                                        {aggregates.maxTemp} °C
                                    </p>
                                </div>
                                <div className="glass-card glow-border text-center p-6 transform hover:scale-105 transition-transform duration-300">
                                    <h3 className="text-neonPink text-xl font-bold mb-2">💧 Humidity (Avg)</h3> {/* AVG is usually better for humidity */}
                                    <p className="text-4xl text-green-400 font-extrabold animate-glow">
                                        {aggregates.avgHumidity} %
                                    </p>
                                </div>
                                <div className="glass-card glow-border text-center p-6 transform hover:scale-105 transition-transform duration-300">
                                     <h3 className="text-purple-400 text-xl font-bold mb-2">💨 Pressure (Avg)</h3>
                                     <p className="text-4xl text-purple-300 font-extrabold animate-glow">
                                         {aggregates.avgPressure} hPa
                                     </p>
                                 </div>
                                <div className="glass-card glow-border text-center p-6 transform hover:scale-105 transition-transform duration-300">
                                    <h3 className="text-yellow-300 text-xl font-bold mb-2">📅 Last Update</h3>
                                    <p className="text-2xl text-gray-300 font-semibold">
                                        {latestReading ? formatTime(latestReading.timestamp) : "--"}
                                    </p>
                                </div>
                            </div>

                            {/* Real-Time Chart (Kept as is) */}
                            <div className="glass-card p-6 glow-border mb-8">
                                <h3 className="text-2xl font-bold mb-4 text-neonBlue custom-glow-text">
                                    📈 Sensor Trends
                                </h3>
                                {deviceData.length > 1 ? ( 
                                    <ResponsiveContainer width="100%" height={350}>
                                        <LineChart data={deviceData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                            <XAxis 
    dataKey="timestamp" 
    tickFormatter={formatTime} 
    stroke="#aaa" 
    type="number" 
    domain={['dataMin', 'dataMax']}
/>
                                            <YAxis yAxisId="left" stroke="#aaa" />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Line yAxisId="left" type="monotone" dataKey="temperature" stroke="#ff00ff" strokeWidth={2.5} name="Temperature (°C)" dot={false} isAnimationActive={false} filter="drop-shadow(0 0 4px #ff00ff)" />
                                            <Line yAxisId="left" type="monotone" dataKey="humidity" stroke="#00ffff" strokeWidth={2.5} name="Humidity (%)" dot={false} isAnimationActive={false} filter="drop-shadow(0 0 4px #00ffff)"/>
                                            <Line yAxisId="left" type="monotone" dataKey="pressure" stroke="#facc15" strokeWidth={2.5} name="Pressure (hPa)" dot={false} isAnimationActive={false} filter="drop-shadow(0 0 4px #facc15)" connectNulls={true} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                     <p className="text-center text-gray-500 py-10">Waiting for more data points to display chart...</p>
                                )}
                            </div>

                            {/* --- Recent Data Table (Coloring Fix) --- */}
                            <div className="glass-card p-6 glow-border">
                                <h3 className="text-2xl font-bold mb-4 text-neonPink custom-glow-text">
                                    📋 Recent Data (Newest First)
                                </h3>
                                <div className="overflow-x-auto">
                                    {deviceData.length > 0 ? (
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-gray-800 text-neonPink">
                                                    <th className="p-3">Temperature (°C)</th>
                                                    <th className="p-3">Humidity (%)</th>
                                                    <th className="p-3">Pressure (hPa)</th>
                                                    <th className="p-3">Status</th>
                                                    <th className="p-3">Timestamp</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Reverse and slice here to show newest 10 */}
                                                {[...deviceData].reverse().slice(0, 10).map((row, i) => (
                                                    <tr key={i} className="hover:bg-gray-800/60 transition-colors duration-200">
                                                        <td className="p-3 text-orange-400">{row.temperature?.toFixed(2) ?? 'N/A'}</td>
                                                        <td className="p-3 text-green-400">{row.humidity?.toFixed(2) ?? 'N/A'}</td>
                                                        <td className="p-3 text-purple-300">{row.pressure?.toFixed(2) ?? 'N/A'}</td>
                                                        
                                                        {/* ✅ STATUS COLORING FIX */}
                                                        <td className={`p-3 font-semibold ${
                                                            row.status === 'Warning'
                                                                ? 'text-yellow-500' 
                                                                : row.status === 'Error'
                                                                ? 'text-red-500' 
                                                                : row.status === 'OK'
                                                                ? 'text-green-500' 
                                                                : 'text-gray-400' 
                                                        }`}>{row.status || 'N/A'}</td>
                                                        
                                                        <td className="p-3 text-gray-400">{formatTime(row.timestamp)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p className="text-center text-gray-500 py-4">No recent data available for this device.</p>
                                    )}
                                </div>
                            </div>
                        </div> // End device section div
                    );
                }) // End map filteredDeviceIds
            )} {/* End ternary */}
        </div> // End main div
    );
}